import { faPen as EditIcon } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { MDXEditorMethods } from '@mdxeditor/editor';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import dayjs from 'dayjs';
import { useAtom } from 'jotai';
import { useEffect, useMemo, useRef } from 'react';
import { Autosave } from 'react-autosave';
import { FormProvider, type SubmitHandler, useForm } from 'react-hook-form';
import { useLocation, useNavigate, useParams } from 'react-router';

import CheckPermissions from '@/components/CheckPermissions';
import LoadingIndicator from '@/components/LoadingIndicator';
import SearchableSelect from '@/components/SearchableSelect';
import { PERMISSIONS } from '@/constants/auth.constants';
import TicketCreateAlerts from '@/features/TicketCreateAlerts';
import TicketCreateEventDetails from '@/features/TicketCreateEventDetails';
import TicketCreateGeneralInformation from '@/features/TicketCreateGeneralInformation';
import TicketCreateRecommendations from '@/features/TicketCreateRecommendations';
import TicketCreateThreatDetails from '@/features/TicketCreateThreatDetails';
import useConfirm from '@/hooks/useConfirm';
import Header from '@/layout/Header/Header';
import MainContent from '@/layout/MainContent';
import { selectedOrganizationAtom } from '@/pages/Organizations';
import {
  EMPTY_TICKET_PAGE,
  mapWatchersDTO,
  setFollowupDate,
  ticketAtom,
  ticketIsEditingAtom,
  useCreateTicketPage,
  useFetchTicketTemplates,
  useFetchWatchersList,
  useUpdateTicketPage
} from '@/pages/Ticket';
import type { TicketTemplatesDTO } from '@/pages/Ticket/Ticket.types';
import { isLayoutMobileAtom } from '@/store/layout';
import { userAtom } from '@/store/user.store';
import { getSessionStorageData } from '@/utils/Common.utils';

import { useFetchTicket } from '../Ticket.store';
import useStyles from './TicketCreate.styles';
import type { TicketCreateSubmission } from './TicketCreate.types';

const TICKET_TEMPLATES_SEARCHABLE_SELECT = {
  field: 'templates',
  title: 'Templates',
  config: {
    searchable: true,
    showCounts: false,
    variant: 'dropdown'
  }
};

const TicketCreate = () => {
  const [isLayoutMobile] = useAtom(isLayoutMobileAtom);
  const [selectedOrganization] = useAtom(selectedOrganizationAtom);
  const [ticket, setTicket] = useAtom(ticketAtom);
  const {
    followup_date,
    id: ticket_id,
    detected_at,
    organization,
    owner,
    ticket_category,
    ticket_type,
    ticket_watchers
  } = ticket;

  const [ticketIsEditing, setTicketIsEditing] = useAtom(ticketIsEditingAtom);
  const [userData] = useAtom(userAtom);

  const { isFetching: isTicketTemplatesFetching, data: ticketTemplates } =
    useFetchTicketTemplates();
  const { watchersPageOptions } = useFetchWatchersList(
    'page',
    ticketIsEditing ? 'selectedTicketOrg' : 'selectedOrg'
  );
  const watcherOptions = useMemo(
    () => mapWatchersDTO(watchersPageOptions),
    [watchersPageOptions]
  );
  const ticketWatchers = useMemo(() => {
    const filteredWatchers = ticket_watchers.filter((watcher) => {
      return watcherOptions.some((option) => {
        return option.userId === watcher.userId;
      });
    });
    return filteredWatchers;
  }, [ticket_watchers, watcherOptions]);
  const currentUserWatcherObject = useMemo(
    () => watcherOptions.find((watcher) => watcher.id === userData?.id),
    [watcherOptions, userData?.id]
  );

  const getDefaultValues = () => {
    // editing ticket default values
    if (ticketIsEditing !== null && ticketIsEditing) {
      return {
        ...ticket,
        category: ticket_category?.id,
        followup_date: setFollowupDate(followup_date),
        detected_at: dayjs(detected_at),
        owner: owner?.id,
        ticket_type,
        ticket_watchers: ticketWatchers
      };
    }

    // creating ticket (threat) and draft exist default values
    const savedDraft = getSessionStorageData('draft');

    if (ticketIsEditing !== null && !ticketIsEditing && savedDraft) {
      const savedValues = {
        ...EMPTY_TICKET_PAGE,
        ...savedDraft,
        followup_date: setFollowupDate(savedDraft.followup_date),
        detected_at:
          savedDraft.detected_at === null
            ? null
            : dayjs(savedDraft.detected_at),
        organization: selectedOrganization.id,
        owner: savedDraft.owner,
        ticket_type,
        ticket_watchers: savedDraft.ticket_watchers || []
      };
      return savedValues;
    }

    // creating ticket (threat) and draft doesnt exist default values
    return {
      ...EMPTY_TICKET_PAGE,
      organization: selectedOrganization.id,
      ticket_watchers: currentUserWatcherObject
        ? [currentUserWatcherObject]
        : []
    };
  };

  const form = useForm({
    defaultValues: getDefaultValues()
  });
  const { getValues, handleSubmit, formState, reset, setValue } = form;
  const location = useLocation();
  const { id } = useParams();
  const ref = useRef<MDXEditorMethods>(null);
  const navigate = useNavigate();
  const { classes } = useStyles({
    userIsInternal: userData?.internal || false
  });

  const { ConfirmationModal: CancelEditModal, confirm: confirmCancel } =
    useConfirm({
      title: ticketIsEditing
        ? 'Cancel Editing Ticket'
        : 'Cancel Creating Ticket',
      message: 'You will lose any unsaved changes.',
      button: 'Confirm'
    });

  let parsedTicketId: number;

  if (id) {
    parsedTicketId = parseInt(id, 10);
  } else {
    parsedTicketId = -1;
  }
  const { isFetching: isTicketFetching } = useFetchTicket(
    parsedTicketId,
    location.pathname
  );

  const ticketTemplateOptions = ticketTemplates.map(
    (template: TicketTemplatesDTO) => ({
      value: template.id,
      label: template.template_name
    })
  );

  const setTicketTemplate = (value: string | number) => {
    const numericValue =
      typeof value === 'string' ? parseInt(value, 10) : value;
    const ticketTemplate = ticketTemplates.find(
      (template: TicketTemplatesDTO) => template.id === numericValue
    );

    if (ticketTemplate) {
      const recommendations =
        ticketTemplate.recommendations &&
        JSON.parse(ticketTemplate.recommendations);

      /* set values */
      setValue('owner', ticketTemplate.owner_id || '');
      setValue('summary', ticketTemplate.summary || '');
      setValue('team_queue', ticketTemplate.team_queue || '');
      setValue('ticket_category', ticketTemplate.category_id);
      setValue('ticket_priority', ticketTemplate.priority_id);
      setValue('ticket_recommendations', recommendations);
      setValue('title', ticketTemplate.title);

      // updates the summary field markdown directly so the ui reflects the change
      ref.current?.setMarkdown(ticketTemplate.summary as string);
    }
  };

  const handleAutoSave = () => {
    const savedDraft = getSessionStorageData('draft');
    if (savedDraft) {
      const draft = { ...getValues(), saved: Date.now() };
      sessionStorage.setItem('draft', JSON.stringify(draft));
    } else {
      const draft = { ...getDefaultValues(), saved: Date.now() };
      sessionStorage.setItem('draft', JSON.stringify(draft));
    }
  };

  const handleCancel = async () => {
    // editing ticket (modified form fields)
    if (ticketIsEditing && formState.isDirty) {
      const confirmed = await confirmCancel();
      if (confirmed) {
        navigate(`/ticket/${ticket.id}`);
      }
      return;
    }

    // editing ticket (no modified form fields)
    if (ticketIsEditing && !formState.isDirty) {
      navigate(`/ticket/${ticket.id}`);
      return;
    }

    // creating ticket
    const confirmed = await confirmCancel();
    if (confirmed) {
      sessionStorage.removeItem('draft');
      navigate(-1);
    }
  };

  const handleClear = () => {
    sessionStorage.removeItem('draft');
    reset({ ...getDefaultValues() });
    ref?.current?.setMarkdown('');
  };

  const handleTitle = useMemo(() => {
    if (ticketIsEditing) {
      if (selectedOrganization.id !== organization?.id) {
        return (
          <>
            Edit Ticket #{ticket_id} for{' '}
            <Typography
              className={classes.columnLeftOrganizationName}
              sx={{ color: 'warning.dark' }}
            >
              {organization?.name}
            </Typography>
          </>
        );
      }
      return `Edit Ticket #${ticket_id}`;
    }
    return 'Create Ticket';
  }, [selectedOrganization.id, ticket_id, ticketIsEditing]);

  const createTicket = useCreateTicketPage();
  const updateTicket = useUpdateTicketPage();

  const onSubmit: SubmitHandler<TicketCreateSubmission> = (data) => {
    if (ticketIsEditing) {
      // handle update
      updateTicket.mutate({ data, ticket });
    } else {
      // handle create
      createTicket.mutate({ data, ticket });
    }
    return true;
  };

  // set the current user as a watcher
  useEffect(() => {
    const savedDraft = getSessionStorageData('draft');

    if (
      !ticketIsEditing &&
      currentUserWatcherObject &&
      savedDraft &&
      savedDraft.ticket_watchers.length === 0
    ) {
      setValue('ticket_watchers', [currentUserWatcherObject]);
    }
  }, [watcherOptions]);

  // creating a ticket, reset selected watchers when organization changes
  useEffect(() => {
    if (!ticketIsEditing && currentUserWatcherObject) {
      setValue('ticket_watchers', [currentUserWatcherObject]);
    }
  }, [selectedOrganization]);

  useEffect(() => {
    setTicketIsEditing(location.pathname.includes('edit'));
  }, [location.pathname, setTicketIsEditing]);

  useEffect(() => {
    if (ticketIsEditing !== null) {
      if (location.pathname.includes('edit')) {
        reset({}, { keepValues: true });
      } else {
        reset(getDefaultValues());

        // ensure ui reflects threat ticket (user could have previously been editing a service ticket)
        // resets ticket_type to 1 (threat)
        const newTicketAtomValues = {
          ...EMPTY_TICKET_PAGE
        };
        setTicket(newTicketAtomValues);
      }
    }
  }, [ticketIsEditing]);

  // set the default value of the next action select
  useEffect(() => {
    if (!ticketIsEditing) {
      setValue(
        'next_action',
        userData?.internal === false ? 'pondurance' : 'customer'
      );
    }
  }, [userData]);

  // Clear router state after consuming caseInfo to prevent re-application after refresh
  useEffect(() => {
    const caseInfo = location.state?.caseInfo;
    if (ticketIsEditing !== false || !caseInfo) {
      return;
    }

    // Merge caseInfo with current form values
    const mergedValues = {
      ...getValues(),
      ...caseInfo
    };
    reset(mergedValues);
    // Clear the router state to prevent persistence after refresh while preserving the URL
    navigate(`${location.pathname}${location.search}${location.hash}`, {
      replace: true,
      state: {}
    });
  }, [
    getValues,
    location.hash,
    location.pathname,
    location.search,
    location.state,
    navigate,
    reset,
    ticketIsEditing
  ]);

  return isTicketFetching ? (
    <LoadingIndicator isLoading={isTicketFetching} />
  ) : (
    <FormProvider {...form}>
      <form
        className={classes.ticketCreateForm}
        onSubmit={handleSubmit(onSubmit)}
        noValidate
      >
        <Header
          title={handleTitle}
          actionsNewLine
          actions={
            <Stack
              direction={isLayoutMobile ? 'column' : 'row'}
              sx={{ width: '100%', gap: 2 }}
            >
              <Stack direction="row" sx={{ width: '100%', gap: 2 }}>
                {!ticketIsEditing && (
                  <Button
                    sx={{ textWrap: 'nowrap' }}
                    type="button"
                    variant={isLayoutMobile ? 'outlined' : 'text'}
                    onClick={handleClear}
                    {...(isLayoutMobile && {
                      fullWidth: true
                    })}
                  >
                    Clear Form
                  </Button>
                )}
                <Button
                  type="button"
                  variant={isLayoutMobile ? 'outlined' : 'text'}
                  onClick={handleCancel}
                  {...(isLayoutMobile && { fullWidth: true })}
                >
                  Cancel
                </Button>
                {!ticketIsEditing && (
                  <SearchableSelect
                    key={TICKET_TEMPLATES_SEARCHABLE_SELECT.field}
                    field={TICKET_TEMPLATES_SEARCHABLE_SELECT.field}
                    filterOptions={
                      (!isTicketTemplatesFetching && ticketTemplateOptions) ||
                      []
                    }
                    multiselect={false}
                    searchable={
                      TICKET_TEMPLATES_SEARCHABLE_SELECT.config.searchable
                    }
                    showCounts={
                      TICKET_TEMPLATES_SEARCHABLE_SELECT.config.showCounts
                    }
                    selectedOptions={[]}
                    titleButton="split-button"
                    titleButtonProps={{
                      fullWidth: true,
                      label: 'Templates',
                      variant: 'contained'
                    }}
                    onSelect={setTicketTemplate}
                  />
                )}
              </Stack>
              <Button
                disabled={
                  (!formState.isDirty && ticketIsEditing) ||
                  (selectedOrganization.id === 0 && !ticketIsEditing)
                }
                fullWidth
                startIcon={<FontAwesomeIcon icon={EditIcon} size="xs" />}
                sx={{ textWrap: 'nowrap' }}
                type="submit"
                variant="contained"
              >
                {ticketIsEditing ? 'Save' : 'Create Draft'}
              </Button>
            </Stack>
          }
        />
        <MainContent>
          <TicketCreateAlerts />
          <div className={classes.ticketCreate}>
            <div className={classes.ticketCreateColumnLeft}>
              <TicketCreateEventDetails ref={ref} />
              {ticket_type === 1 && <TicketCreateRecommendations />}
              {ticket_type === 1 && <TicketCreateThreatDetails />}
            </div>
            <CheckPermissions permissions={PERMISSIONS.INTERNAL}>
              <div className={classes.ticketCreateColumnRight}>
                <TicketCreateGeneralInformation />
              </div>
            </CheckPermissions>
          </div>
        </MainContent>
        <CancelEditModal />
      </form>
      {!ticketIsEditing && (
        <Autosave data={getValues()} onSave={handleAutoSave} />
      )}
    </FormProvider>
  );
};

export default TicketCreate;
